import { BrandLockup } from "../BrandLockup";
import { Link, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  ACCENT_BAR_BEFORE,
  ACCENT_TINT_SOFT,
  ACCENT_TINT_SOFT_HOVER,
  ACCENT_TINT_STRONG,
  SIDENAV_CANVAS,
  SIDENAV_ORB_OVERLAY,
  SIDENAV_SHADOW,
} from "../../constants/shellTheme";
import { Box, Loader } from "@saviynt/design-system";

export const SIDENAV_EXPANDED_WIDTH = 240;
export const SIDENAV_COLLAPSED_WIDTH = 60;
const EXPANDED_WIDTH = SIDENAV_EXPANDED_WIDTH;
const COLLAPSED_WIDTH = SIDENAV_COLLAPSED_WIDTH;
const SLIDE_DURATION = 300;
const SIDE_MENU_Z_INDEX = 1010;

export type SidenavSection = "workspace" | "system";

export interface SidenavSubItem {
  subNavItem: string;
  label?: string;
  /** Explicit route. Falls back to `/<parent>/<...ancestors>/<subNavItem>`. */
  path?: string;
  children?: SidenavSubItem[];
  badge?: string;
}

export interface SidenavNavItem {
  navItem: string;
  label?: string;
  icon?: React.ReactNode;
  /** Explicit route for a leaf item. Falls back to `/<navItem>`. */
  path?: string;
  subNav?: SidenavSubItem[];
  section: SidenavSection;
}

const NAV_ITEM_BASE =
  "group relative flex items-center py-2 mb-0 rounded-lg " +
  "text-[12px] font-normal leading-5 capitalize select-none " +
  "transition-all duration-200 ease-in-out";

/** Wraps a nav icon so it scales up subtly when its row is hovered. */
const ICON_WRAP =
  "inline-flex flex-shrink-0 transition-transform duration-200 group-hover:scale-110";

/**
 * Per-item open delays for the staggered sub-item reveal. Full Tailwind
 * `delay-*` class literals (not interpolated) so the JIT scanner emits them;
 * indexed by sub-item position and capped at the last entry.
 */
const STAGGER_DELAYS = [
  "delay-0",
  "delay-[35ms]",
  "delay-[70ms]",
  "delay-[105ms]",
  "delay-[140ms]",
  "delay-[175ms]",
];

const NAV_ITEM_LAYOUT = {
  expanded: "gap-[13px] pl-[6px] pr-[6px]",
  collapsed: "justify-center px-0",
} as const;

const NAV_ITEM_INACTIVE = {
  expanded: `text-slate-400 ${ACCENT_TINT_SOFT_HOVER} hover:text-white hover:translate-x-0.5`,
  collapsed: `text-slate-400 ${ACCENT_TINT_SOFT_HOVER} hover:text-white`,
} as const;

const NAV_ITEM_ACTIVE = {
  expanded:
    `${ACCENT_TINT_SOFT} text-white ` +
    "before:content-[''] before:absolute before:-left-3 before:top-[9px] before:bottom-[9px] " +
    `before:w-0.5 ${ACCENT_BAR_BEFORE} before:rounded-r-sm`,
  collapsed: `${ACCENT_TINT_STRONG} text-white`,
} as const;

const SUB_ITEM_BASE =
  "relative block px-3 py-2 text-[12px] font-normal rounded-md capitalize " +
  "transition-all duration-200 ease-in-out";

const SUB_ITEM_INACTIVE = `text-slate-400 ${ACCENT_TINT_SOFT_HOVER} hover:text-white hover:translate-x-0.5`;
const SUB_ITEM_ACTIVE = `${ACCENT_TINT_STRONG} text-white font-medium`;

const SideMenuIconBack = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M20.5 6.16675C20.7761 6.16675 21 6.39061 21 6.66675C21 6.94289 20.7761 7.16675 20.5 7.16675H9.5C9.22386 7.16675 9 6.94289 9 6.66675C9 6.39061 9.22386 6.16675 9.5 6.16675H20.5Z"
      fill="currentColor"
    />
    <path
      d="M20.5 11.5001C20.7761 11.5001 21 11.7239 21 12.0001C21 12.2762 20.7761 12.5001 20.5 12.5001H4.70719L6.5203 14.3132C6.71556 14.5085 6.71556 14.825 6.5203 15.0203C6.32504 15.2156 6.00846 15.2156 5.81319 15.0203L3.15188 12.359C3.05821 12.2681 3 12.1409 3 12.0001C3 11.8793 3.04285 11.7685 3.11418 11.682C3.12437 11.6697 3.13514 11.6578 3.14645 11.6465C3.14643 11.6465 3.14646 11.6465 3.14645 11.6465L5.8132 8.97986C6.00847 8.7846 6.32505 8.7846 6.52031 8.97987C6.71557 9.17513 6.71556 9.49172 6.5203 9.68697L4.70713 11.5001H20.5Z"
      fill="currentColor"
    />
    <path
      d="M21 17.3334C21 17.0573 20.7761 16.8334 20.5 16.8334H9.5C9.22386 16.8334 9 17.0573 9 17.3334C9 17.6096 9.22386 17.8334 9.5 17.8334H20.5C20.7761 17.8334 21 17.6096 21 17.3334Z"
      fill="currentColor"
    />
  </svg>
);

const SideMenuSmallIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M3.50012 6.16675C3.22398 6.16675 3.00012 6.39061 3.00012 6.66675C3.00012 6.94289 3.22398 7.16675 3.50012 7.16675H14.5001C14.7763 7.16675 15.0001 6.94289 15.0001 6.66675C15.0001 6.39061 14.7763 6.16675 14.5001 6.16675H3.50012Z"
      fill="currentColor"
    />
    <path
      d="M3.50012 11.5001C3.22398 11.5001 3.00012 11.7239 3.00012 12.0001C3.00012 12.2762 3.22398 12.5001 3.50012 12.5001H19.2929L17.4798 14.3132C17.2846 14.5085 17.2846 14.825 17.4798 15.0203C17.6751 15.2156 17.9917 15.2156 18.1869 15.0203L20.8482 12.359C20.9419 12.2681 21.0001 12.1409 21.0001 12.0001C21.0001 11.8793 20.9573 11.7685 20.8859 11.682C20.8758 11.6697 20.865 11.6578 20.8537 11.6465C20.8537 11.6465 20.8537 11.6465 20.8537 11.6465L18.1869 8.97986C17.9917 8.7846 17.6751 8.7846 17.4798 8.97987C17.2846 9.17513 17.2846 9.49172 17.4798 9.68697L19.293 11.5001H3.50012Z"
      fill="currentColor"
    />
    <path
      d="M3.00012 17.3334C3.00012 17.0573 3.22398 16.8334 3.50012 16.8334H14.5001C14.7763 16.8334 15.0001 17.0573 15.0001 17.3334C15.0001 17.6096 14.7763 17.8334 14.5001 17.8334H3.50012C3.22398 17.8334 3.00012 17.6096 3.00012 17.3334Z"
      fill="currentColor"
    />
  </svg>
);

const SideMenuIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.5715 8.28564C19.0448 8.28564 19.4286 8.6694 19.4286 9.14279C19.4286 9.61618 19.0448 9.99993 18.5715 9.99993H4.85714C4.38376 9.99993 4 9.61618 4 9.14279C4 8.6694 4.38376 8.28564 4.85714 8.28564H18.5715Z"
      fill="currentColor"
    />
    <path
      d="M28 15.992C27.998 15.7753 27.9143 15.5592 27.749 15.3938L24.749 12.3938C24.4142 12.0591 23.8715 12.0591 23.5368 12.3938C23.2021 12.7286 23.2021 13.2713 23.5368 13.606L25.0736 15.1428H4.85714C4.38376 15.1428 4 15.5266 4 15.9999C4 16.4733 4.38376 16.8571 4.85714 16.8571H25.0736L23.5368 18.3939C23.2021 18.7286 23.2021 19.2713 23.5368 19.606C23.8715 19.9408 24.4142 19.9408 24.749 19.606L27.749 16.606C27.9143 16.4407 27.998 16.2246 28 16.0079"
      fill="currentColor"
    />
    <path
      d="M19.4286 22.8571C19.4286 22.3837 19.0448 22 18.5715 22H4.85714C4.38376 22 4 22.3837 4 22.8571C4 23.3305 4.38376 23.7142 4.85714 23.7142H18.5715C19.0448 23.7142 19.4286 23.3305 19.4286 22.8571Z"
      fill="currentColor"
    />
  </svg>
);

interface SidenavProps {
  navItems: SidenavNavItem[];
  isLoading?: boolean;
  isMenuPinned?: boolean;
  onPinnedChange?: (pinned: boolean) => void;
  isMenuExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

/**
 * Keys of the groups that should start expanded so the active route's item is
 * visible on load. Walks the tree mirroring the path-resolution logic and
 * returns the top-level navItem key plus any nested group keys on the path.
 */
function getActiveExpandedKeys(
  navItems: SidenavNavItem[],
  currentPath: string,
): string[] {
  const matches = (target: string) =>
    currentPath === target || currentPath.startsWith(`${target}/`);
  const keys: string[] = [];

  navItems.forEach((item) => {
    if (!item.subNav?.length) return;

    const walk = (subItems: SidenavSubItem[], ancestors: string[]): boolean => {
      let found = false;
      subItems.forEach((sub) => {
        const subKey = [item.navItem, ...ancestors, sub.subNavItem].join("/");
        if (sub.children?.length) {
          if (walk(sub.children, [...ancestors, sub.subNavItem])) {
            keys.push(subKey);
            found = true;
          }
        } else {
          const path =
            sub.path ??
            `/${item.navItem}/${[...ancestors, sub.subNavItem].join("/")}`;
          if (matches(path)) found = true;
        }
      });
      return found;
    };

    if (walk(item.subNav, [])) keys.push(item.navItem);
  });

  return keys;
}

/**
 * Animates its children open/closed by transitioning grid-template-rows
 * between 0fr and 1fr (smooth height with no JS measurement), paired with a
 * fade. Children stay mounted so the collapse can animate both ways.
 */
function Collapsible({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      }`}
    >
      <div
        className={`overflow-hidden transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export function Sidenav({
  navItems,
  isLoading = false,
  isMenuPinned: pinnedProp,
  onPinnedChange,
  isMenuExpanded: expandedProp,
  onExpandedChange,
}: SidenavProps) {
  const intl = useIntl();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    () => new Set(getActiveExpandedKeys(navItems, currentPath)),
  );
  const [searchQuery, setSearchQuery] = useState("");

  const [internalPinned, setInternalPinned] = useState(true);
  const [internalExpanded, setInternalExpanded] = useState(true);
  const [isMenuClosing, setIsMenuClosing] = useState(false);

  const isMenuPinned = pinnedProp ?? internalPinned;
  const isExpanded = expandedProp ?? internalExpanded;

  const setIsMenuPinned = (next: boolean) => {
    if (onPinnedChange) onPinnedChange(next);
    else setInternalPinned(next);
  };
  const setIsExpanded = (next: boolean) => {
    if (onExpandedChange) onExpandedChange(next);
    else setInternalExpanded(next);
  };

  const toggleExpanded = (key: string) => {
    const next = new Set(expandedItems);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpandedItems(next);
  };

  const matchesQuery = (label: string | undefined, id: string) =>
    (label ?? id).toLowerCase().includes(searchQuery.toLowerCase()) ||
    id.toLowerCase().includes(searchQuery.toLowerCase());

  const filteredNavItems = navItems.filter((item) => {
    const itemMatches = matchesQuery(item.label, item.navItem);
    const subItemMatches = item.subNav?.some((sub) => {
      const subMatches = matchesQuery(sub.label, sub.subNavItem);
      const childMatches = sub.children?.some((child) =>
        matchesQuery(child.label, child.subNavItem),
      );
      return subMatches || childMatches;
    });
    return itemMatches || subItemMatches;
  });

  const searchActive = searchQuery.trim().length > 0;

  // While searching, auto-expand any group (and nested group) that contains a
  // match so the matched items are visible without a manual click. This is a
  // derived overlay only — it never mutates the user's own expand/collapse
  // state, so clearing the search restores exactly what they had open.
  const searchExpandedKeys = ((): Set<string> | null => {
    if (!searchActive) return null;
    const keys = new Set<string>();
    const walk = (
      navItem: string,
      subItems: SidenavSubItem[],
      ancestors: string[],
    ): boolean => {
      let anyMatch = false;
      subItems.forEach((sub) => {
        const subKey = [navItem, ...ancestors, sub.subNavItem].join("/");
        const selfMatch = matchesQuery(sub.label, sub.subNavItem);
        let childMatch = false;
        if (sub.children?.length) {
          childMatch = walk(navItem, sub.children, [
            ...ancestors,
            sub.subNavItem,
          ]);
          if (childMatch) keys.add(subKey);
        }
        if (selfMatch || childMatch) anyMatch = true;
      });
      return anyMatch;
    };
    filteredNavItems.forEach((item) => {
      if (!item.subNav?.length) return;
      const hasMatch = walk(item.navItem, item.subNav, []);
      if (hasMatch || matchesQuery(item.label, item.navItem)) {
        keys.add(item.navItem);
      }
    });
    return keys;
  })();

  const isKeyExpanded = (key: string) =>
    expandedItems.has(key) || (searchExpandedKeys?.has(key) ?? false);

  const workspaceItems = filteredNavItems.filter(
    (i) => i.section === "workspace",
  );
  const systemItems = filteredNavItems.filter((i) => i.section === "system");

  const handleMouseEnter = () => {
    if (!isMenuClosing) setIsExpanded(true);
  };
  const handleMouseLeave = () => {
    if (!isMenuPinned) setIsExpanded(false);
  };

  const handlePinClick = () => {
    if (isMenuPinned) {
      setIsMenuClosing(true);
      setIsExpanded(false);
      setTimeout(() => setIsMenuClosing(false), SLIDE_DURATION);
    } else {
      setIsExpanded(true);
    }
    setIsMenuPinned(!isMenuPinned);
  };

  const showExpanded = isExpanded || isMenuPinned;

  const renderSubItem = (
    subItem: SidenavSubItem,
    parentNavItem: string,
    ancestorPath: string[] = [],
  ) => {
    const subItemKey = [
      parentNavItem,
      ...ancestorPath,
      subItem.subNavItem,
    ].join("/");
    const isNestedExpanded = isKeyExpanded(subItemKey);

    if (subItem.children?.length) {
      return (
        <div key={subItem.subNavItem}>
          <button
            type="button"
            onClick={() => toggleExpanded(subItemKey)}
            aria-expanded={isNestedExpanded}
            className={`${SUB_ITEM_BASE} ${SUB_ITEM_INACTIVE} flex w-full items-center justify-between text-left`}
          >
            <span className="flex items-center gap-1.5">
              {subItem.label ?? subItem.subNavItem.replace(/-/g, " ")}
              {subItem.badge && (
                <span className="normal-case bg-gradient-to-t from-[#ef4444] to-[#fda4af] bg-clip-text text-[8px] font-semibold leading-none text-transparent">
                  {subItem.badge}
                </span>
              )}
            </span>
            <svg
              width="12"
              height="12"
              className={`ml-2 flex-shrink-0 text-slate-500 transition-transform duration-200 ${isNestedExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          <Collapsible open={isNestedExpanded}>
            <div className="ml-2 mt-0.5 space-y-0.5 pl-2">
              {subItem.children.map((child) => {
                const childPath =
                  child.path ??
                  `/${parentNavItem}/${[...ancestorPath, subItem.subNavItem, child.subNavItem].join("/")}`;
                const isActive = currentPath === childPath;
                return (
                  <Link
                    key={child.subNavItem}
                    to={childPath}
                    data-testid={`sidenav-sublink-${child.subNavItem}`}
                    className={`${SUB_ITEM_BASE} ${isActive ? SUB_ITEM_ACTIVE : SUB_ITEM_INACTIVE}`}
                  >
                    <span className="flex items-center gap-1.5">
                      {child.label ?? child.subNavItem.replace(/-/g, " ")}
                      {child.badge && (
                        <span className="normal-case bg-gradient-to-t from-[#ef4444] to-[#fda4af] bg-clip-text text-[8px] font-semibold leading-none text-transparent">
                          {child.badge}
                        </span>
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>
          </Collapsible>
        </div>
      );
    }

    const linkPath =
      subItem.path ??
      `/${parentNavItem}/${[...ancestorPath, subItem.subNavItem].join("/")}`;
    const isActive = currentPath === linkPath;
    return (
      <Link
        key={subItem.subNavItem}
        to={linkPath}
        data-testid={`sidenav-sublink-${subItem.subNavItem}`}
        className={`${SUB_ITEM_BASE} ${isActive ? SUB_ITEM_ACTIVE : SUB_ITEM_INACTIVE}`}
      >
        <span className="flex items-center gap-1.5">
          {subItem.label ?? subItem.subNavItem.replace(/-/g, " ")}
          {subItem.badge && (
            <span className="normal-case bg-gradient-to-t from-[#ef4444] to-[#fda4af] bg-clip-text text-[8px] font-semibold leading-none text-transparent">
              {subItem.badge}
            </span>
          )}
        </span>
      </Link>
    );
  };

  const renderNavItem = (item: SidenavNavItem) => {
    const linkPath = item.path ?? `/${item.navItem}`;
    const isActive = currentPath === linkPath;
    const isGroupOpen = !!item.subNav && isKeyExpanded(item.navItem);
    const variant = showExpanded ? "expanded" : "collapsed";
    const layoutCls = NAV_ITEM_LAYOUT[variant];
    const inactiveCls = NAV_ITEM_INACTIVE[variant];
    const activeCls = NAV_ITEM_ACTIVE[variant];

    return (
      <div key={item.navItem} className="flex flex-col">
        {item.subNav ? (
          <button
            onClick={() => toggleExpanded(item.navItem)}
            aria-expanded={isGroupOpen}
            className={`${NAV_ITEM_BASE} ${layoutCls} ${inactiveCls} w-full text-left`}
          >
            {showExpanded && (
              <svg
                width="14"
                height="14"
                className={`flex-shrink-0 text-slate-500 transition-transform duration-200 ${isGroupOpen ? "rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            )}
            <span className={ICON_WRAP}>{item.icon}</span>
            {showExpanded && (
              <span>{item.label ?? item.navItem.replace(/-/g, " ")}</span>
            )}
          </button>
        ) : (
          <Link
            to={linkPath}
            className={`${NAV_ITEM_BASE} ${layoutCls} ${isActive ? activeCls : inactiveCls}`}
            data-testid={`sidenav-link-${item.navItem}`}
          >
            {showExpanded && (
              <span className="w-3.5 flex-shrink-0" aria-hidden />
            )}
            <span className={ICON_WRAP}>{item.icon}</span>
            {showExpanded && (
              <span>{item.label ?? item.navItem.replace(/-/g, " ")}</span>
            )}
          </Link>
        )}

        {showExpanded && item.subNav && (
          <Collapsible open={isGroupOpen}>
            <div className="mt-0.5 mb-1.5 space-y-0.5 pl-[38px]">
              {item.subNav.map((subItem, i) => (
                <div
                  key={subItem.subNavItem}
                  className={`transition-transform duration-200 ease-out ${
                    isGroupOpen
                      ? `translate-y-0 ${STAGGER_DELAYS[Math.min(i, STAGGER_DELAYS.length - 1)]}`
                      : "-translate-y-1 delay-0"
                  }`}
                >
                  {renderSubItem(subItem, item.navItem)}
                </div>
              ))}
            </div>
          </Collapsible>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`relative flex h-screen flex-col overflow-hidden flex-shrink-0 whitespace-nowrap border-r border-white/5 ${SIDENAV_CANVAS} ${SIDENAV_SHADOW} transition-[width] duration-[${SLIDE_DURATION}ms] ease-in-out`}
      style={{
        width: showExpanded ? `${EXPANDED_WIDTH}px` : `${COLLAPSED_WIDTH}px`,
        zIndex: SIDE_MENU_Z_INDEX,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="sidenav"
    >
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 ${SIDENAV_ORB_OVERLAY}`}
      />

      <div className="relative z-[1] flex flex-1 flex-col overflow-y-auto overflow-x-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {/* Header */}
        <div
          className={`flex min-h-[68px] items-center border-b border-white/5 ${showExpanded ? "justify-between px-[22px]" : "justify-center px-0"}`}
        >
          {showExpanded && (
            <Link to="/" className="flex items-center">
              <BrandLockup variant="dark" size="sm" />
            </Link>
          )}
          <button
            onClick={handlePinClick}
            className="grid h-[34px] w-[34px] place-items-center rounded-lg border border-white/[0.06] text-slate-400 transition-all duration-200 hover:border-white/10 hover:bg-white/[0.04] hover:text-white"
            data-testid="sidenav-pin-button"
            aria-label={
              isMenuPinned
                ? intl.formatMessage({
                    id: "ztzc4E",
                    defaultMessage: "Unpin menu",
                    description: "Aria label for unpin menu button",
                  })
                : intl.formatMessage({
                    id: "UwFDao",
                    defaultMessage: "Pin menu",
                    description: "Aria label for pin menu button",
                  })
            }
          >
            {showExpanded ? (
              isMenuPinned ? (
                <SideMenuIconBack />
              ) : (
                <SideMenuSmallIcon />
              )
            ) : (
              <SideMenuIcon />
            )}
          </button>
        </div>

        {/* Search */}
        {showExpanded ? (
          <div className="relative px-5 py-[18px]">
            <svg
              className="pointer-events-none absolute left-[34px] top-1/2 -translate-y-1/2 text-slate-500"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder={intl.formatMessage({
                id: "vOoC5M",
                defaultMessage: "Search pages & features",
                description: "Search input placeholder",
              })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="sidenav-search-input"
              className="w-full rounded-lg border border-white/[0.06] bg-white/[0.02] py-[11px] pl-10 pr-[14px] text-[11px] text-white outline-none transition-all duration-200 placeholder:text-slate-500 focus:border-white/10 focus:bg-white/[0.04]"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center py-[18px]">
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="grid h-[34px] w-[34px] place-items-center rounded-lg border border-white/[0.06] text-slate-400 transition-all duration-200 hover:border-white/10 hover:bg-white/[0.04] hover:text-white"
              aria-label={intl.formatMessage({
                id: "K/mueW",
                defaultMessage: "Search",
                description: "Aria label for search button",
              })}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </button>
          </div>
        )}

        {/* Nav */}
        {isLoading ? (
          <Box className="flex justify-center">
            <Loader kind="loop" size="small" format="inline" color="brand" />
          </Box>
        ) : (
          <nav className={`flex-1 pb-3 ${showExpanded ? "px-3" : "px-2"}`}>
            {workspaceItems.map(renderNavItem)}
            {systemItems.length > 0 && <div className="mt-2" />}
            {systemItems.map(renderNavItem)}
          </nav>
        )}

        {/* Footer */}
        {showExpanded && (
          <div className="mt-auto border-t border-white/5 px-[18px] py-4">
            <p className="text-[10px] tracking-wide text-slate-500">
              <FormattedMessage
                id="39yQKP"
                defaultMessage="© Copyright 2026 Saviynt"
                description="Copyright notice in sidebar"
              />
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}
